<template>
  <div>
    <!-- Filters -->
    <div v-if="filtersLocal.length > 0" class="d-flex align-items-end mb-3">
      <div 
        v-for="(filter, index) in filtersLocal"
        :key="filter[index]"
        class="mr-4"
      >
        <b-form-group :label="filter.name">
          <!-- Text, search or number inputs -->
          <b-form-input 
            v-if="filter.type === 'text' || filter.type === 'search' || filter.type === 'number'"
            v-model="filter.value"
            :type="filter.type"
            @input="onSearch"
          ></b-form-input>

          <!-- Form select -->
          <b-form-select
            v-if="filter.type === 'select'"
            v-model="filter.value" 
            :options="filter.options"
            :style="{ width: filter.width }"
            @change="onSearch"
          ></b-form-select>
        </b-form-group>
      </div>

      <b-button size="sm" @click="onClearSearch">Clear</b-button>

      <div class="text-nowrap ml-4"><slot name="filterRight"></slot></div>
    </div>

    <!-- Grid -->
    <div class="grid-component grid-component-sticky mb-3">
      <table class="table table-striped table-hover table-bordered">
        <thead class="thead-light">
          <tr>
            <th 
              v-for="field in fieldsLocal"
              :key="field.key"
              :class="{ 'w-1px': field.key === 'commands' }"
              @click="onHeadClick(field)"
            >
              <!-- Header commands -->
              <div v-if="field.key === 'commands'" class="d-flex align-items-center">
                <!-- Listing of columns to check or uncheck -->
                <grid-settings :fields.sync="fieldsLocal" :grid-id="gridId" />
                
                <!-- Checkbox -->
                <input 
                  v-if="hasCheckbox"
                  v-model="checkAllRows"
                  type="checkbox"
                  class="ml-2" 
                />
              </div>
              
              <!-- For all columns -->
              <template v-else>
                {{ field.label }}

                <i 
                  class="sort-arrow fa fa-arrow-up fa-fw" 
                  :class="{ 
                    invisible: sortByLocal !== field.key, 
                    'rotate-sort-arrow': sortOrderLocal === 'DESC' && sortByLocal === field.key,
                  }"
                ></i>
              </template>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, index) in items"
            :key="index"
            @click="onRowClick(item, index, $event)"
          >
            <td
              v-for="field in fieldsLocal"
              :key="field.key"
            >
              <!-- Row commands -->
              <div v-if="field.key === 'commands'" class="d-flex align-items-center">
                <!-- Edit icon trigger -->
                <i
                  v-if="hasEdit"
                  @click="$emit('edit', item)"
                  :class="{ 'mr-2': hasDelete || hasCheckbox }"
                  class="grid-commands-icon-edit fa fa-edit fa-fw"
                ></i>

                <!-- Checkbox -->
                <input
                  v-if="hasCheckbox"
                  v-model="checkedRowItemIdsLocal"
                  :value="item.id"
                  type="checkbox" 
                  :class="{ 'mr-2': hasDelete }" 
                />

                <!-- Delete icon trigger -->
                <i
                  v-if="hasDelete"
                  @click="$emit('delete', item)"
                  class="grid-commands-icon-delete fa fa-trash fa-fw"
                ></i>
              </div>

              <template v-else>
                {{ item[field.key] }}
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div v-if="hasPagination && totalRows" class="d-flex align-items-center">
      <!-- Button bar to choose page -->
      <b-pagination
        v-model="currentPageLocal"
        :total-rows="totalRows"
        :per-page="perPageLocal"
        :limit="10"
        size="sm"
        class="mb-0 mr-4"
        @input="onChangePagination"
      ></b-pagination>

      <!-- Jump to sepcific page -->
      <b-input 
        v-model="currentPageLocal" 
        type="number" 
        :min="1"
        :max="Math.ceil(totalRows / perPageLocal)"
        class="pagination-jumper mr-4" 
      />

      <!-- Items per page -->
      <b-form-select 
        v-model.number="perPageLocal" 
        class="w-auto mr-2"
        @input="onChangePagination"
      >
        <option value="5">5</option>
        <option value="10">10</option>
        <option value="20">20</option>
        <option value="50">50</option>
      </b-form-select>
      <a>items per page</a>

      <!-- Range -->
      <a class="ml-auto">{{ currentLowerBound }} - {{ currentUpperBound }} / {{ totalRows }} items</a>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash/debounce';
import { SEARCH_DEBOUNCE_TIME } from '@/shared/constants';

import GridSettings from '@/shared/components/GridSettings';

export default {
  components: {
    GridSettings
  },
  props: {
    gridId: { // Grid id used to save settings. REQUIRED
      type: String,
      default: '',
    },
    items: { // Array of Objects of items on grid
      type: Array,
      default: () => [], 
    },
    fields: { // Column names on grid
      type: Array,
      default: () => [],
    },
    // Function to make API call for getting items. Will be automatically called onCreated() and when filtering/sorting.
    getItemsFunction: {
      type: Function,
      default: () => {},
    },
    /* Filters {Object[]}
     *
     * @param {( 'text'|'number'|'select' )} filters.type - The input type.
     * @param {string} filters.name - Name used on form label.
     * @param {string} filters.fieldName - Name of column from database.
     * @param {( [{ value, text }, ] )} filters.options - Array of objects formatted for form select.
     * @param {String} filters.width - Width of form field.
     */
    filters: { // Array of Objects of Filters
      type: Array,
      default: () => [],
    },
    sortBy: { // Field to sort by
      type: String,
      default: '',
    },
    sortOrder: { // Sort order of field sorted by
      type: String,
      default: 'DESC',
    },
    hasEdit: { // Has edit icon
      type: Boolean,
      default: false,
    },
    hasDelete: { // Has delete icon
      type: Boolean,
      default: false,
    },
    hasCheckbox: { // Has checkbox
      type: Boolean,
      default: false,
    },
    hasPagination: { // Has pagination
      type: Boolean,
      default: false,
    },
    checkedRowItemIds: {  // Array of checked rows. Can be used with .sync for two-way binding
      type: Array,
      default: () => [],
    },
    currentPage: { // Pagination current page. Can be used with .sync for two-way binding
      type: [Number, String],
      default: 1,
    },
    totalRows: { // Pagination total rows
      type: [Number, String],
      default: 0,
    },
    perPage: { // Pagination items per page. Can be used with .sync for two-way binding
      type: [Number, String],
      default: 20,
    },
  },
  data() {
    return {
      checkedRowItemIdsLocal: this.checkedRowItemIds,
      fieldsLocal: this.fields.map(field => ({ ...field }) ), // Clone array of objects to prevent mutating prop directly
      filtersLocal: this.filters.map(filter => ({ ...filter }) ), // Clone array of objects to prevent mutating prop directly
      sortByLocal: this.sortBy || '',
      sortOrderLocal: this.sortOrder,
      currentPageLocal: this.currentPage,
      perPageLocal: this.perPage,
    }
  },
  computed: {
    fieldsComputed() {
      return this.fieldsLocal.filter(field => !field.hidden);
    },
    checkAllRows: {
      get() {
        if(this.items && this.items.length > 0) { // An items array exists with at least one item
          let allChecked = true;

          this.items.forEach((item) => {
            if(!this.checkedRowItemIdsLocal.includes(item.id)) {
              allChecked = false; // If even one is not included in array
            }

            // Break out of loop if mismatch already found
            if(!allChecked) return false;
          });

          return allChecked;
        }

        return false;
      },
      set(value) {
        const checked = [];

        if (value) {
          this.items.forEach((item) => {
            checked.push(item.id);
          });
        }

        this.checkedRowItemIdsLocal = checked;
      }
    },
    currentLowerBound() {
      return 1 + (this.currentPageLocal - 1) * this.perPageLocal;
    },
    currentUpperBound() {
      const multiplied = this.currentPageLocal * this.perPageLocal;

      // If there are less rows than amount per page specified
      if(this.totalRows < this.perPageLocal || multiplied > this.totalRows) return this.totalRows;

      return multiplied; 
    },
    maxPaginationAmount() {
      return this.totalRows / this.perPageLocal + 1;
    }
  },
  methods: {
    onSearch: debounce(async function() {
      let filterObj = {};

      this.filtersLocal.forEach((filter) => {
        if(filter.value) {
          filterObj = { ...filterObj, [filter.fieldName]: filter.value };
        }
      });

      await this.getItemsFunction({ filters: filterObj }); // Get results and mutate filters
    }, SEARCH_DEBOUNCE_TIME),
    async onClearSearch() {
      this.filtersLocal.forEach((field, index) => {
        this.filtersLocal[index].value = '';
      });

      await this.getItemsFunction({ filters: {} }); // Clear filters
    },
    onRowClick(item, index, event) {
      
      this.$emit('click-row', item, index, event);
    },
    async onHeadClick({ key, sortable }) {
      if(key === 'commands' || !sortable) return false; // Don't do any sorting on commands column and unsortable fields

      if(this.sortByLocal === key && this.sortOrderLocal === 'DESC') {
        this.sortOrderLocal = 'ASC';
      } else {
        this.sortOrderLocal = 'DESC';
      }

      this.sortByLocal = key;

      await this.getItemsFunction({ sortBy: this.sortByLocal, sortOrder: this.sortOrderLocal });
    },
    async onChangePagination() {
      await this.getItemsFunction({ pageNumber: this.currentPageLocal, pageSize: this.perPageLocal });
    },
  },
  watch: {
    checkedRowItemIdsLocal() {
      // Allows you to use this with .sync for two-way binding
      this.$emit('update:checkedRowItemIds', this.checkedRowItemIdsLocal);
    },
    perPageLocal() {
      // Allows you to use this with .sync for two-way binding
      this.$emit('update:perPage', this.perPageLocal);
    },
    currentPageLocal() {
      // Allows you to use this with .sync for two-way binding
      this.$emit('update:currentPage', this.currentPageLocal);
    },
    filters: {
      handler() {
        // Shallow clone array of objects to prevent prop being mutated directly
        this.filtersLocal = this.filters.map(filter => ({ ...filter }) );
      },
      deep: true,
    },
  },
  async created() {
    if(!this.gridId) console.error(`Prop 'grid-id' is required in <grid> component`);

    await this.getItemsFunction();

    this.fieldsLocal.forEach((field, index) => {
      if(!field.hasOwnProperty('sortable')) {
        this.$set(this.fieldsLocal[index], 'sortable', true); // Explicitly Set values to sortable = false if property doesn't exist
      }
    });
  }
}
</script>

<style>
/* Grid Commands */
.grid-commands-icon-edit { /* Edit icon on table */
  color: #1b8eb7;
}

.grid-commands-icon-delete { /* Delete icon on table */
  color: #f64846;
}

.grid-commands-icon-edit:hover, .grid-commands-icon-delete:hover {
  cursor: pointer;
  opacity: 0.8;
}

.grid-component th {
  cursor: pointer;
}

.grid-component td, .grid-component th {
  padding: 0.25rem;
  color: #656565;
}

.grid-component.b-table > thead > tr > [aria-sort="none"] { /* Remove default sort nothing buttons */
  background-image: none;
}

/* Makes header sticky */
.grid-component-sticky {
  overflow-y: auto;
  max-height: 300px;
}

.grid-component-sticky > table > thead > tr > th {
  position: sticky;
  top: 0;
}

/* Allows row to be selected */
.grid-component-selected-row td {
  border-color: #8bd2eb;
}

.pagination-jumper {
  width: 50px;
}

/* Sorting Arrow */
.sort-arrow {
  transition: transform 0.3s;
}

.rotate-sort-arrow {
  transform: rotate(180deg);
}

/* Used to allow commands column to be auto width */
.w-1px {
  width: 1px;
}
</style>